import React from "react";
import { AlertCircle, CheckCircle2, ChevronRight, HelpCircle, Sparkles, TrendingUp, UtensilsCrossed, Zap } from "lucide-react";
import { inr } from "@/lib/format";

export default function MenuEngineeringWidget({ menuEngineering }) {
  if (!menuEngineering) return null;

  const {
    quadrants = {},
    data_quality = {},
    overall_margin_pct = 0,
    food_cost_pct = 0,
  } = menuEngineering;

  const {
    stars = [],
    plowhorses = [],
    puzzles = [],
    dogs = [],
  } = quadrants;

  const coverageBadge = data_quality.coverage_badge || "Uncosted";
  const costedPct = data_quality.costed_items_count && data_quality.total_items_count
    ? Math.round((data_quality.costed_items_count / data_quality.total_items_count) * 100)
    : 0;

  return (
    <section className="rounded-lg border bg-card p-loose space-y-base" data-testid="dash-menu-engineering">
      <div className="flex flex-wrap items-center justify-between gap-snug border-b pb-snug">
        <div className="flex items-center gap-snug">
          <UtensilsCrossed className="h-5 w-5 text-muted-foreground" />
          <div>
            <h2 className="text-lede font-semibold">Menu Engineering Matrix (BCG)</h2>
            <p className="text-meta text-muted-foreground">
              Profitability vs Popularity classification driven by recipe costs
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-snug">
          <div className="flex items-center gap-tight text-meta">
            <span className="text-muted-foreground">Overall Margin:</span>
            <span className="font-bold tabular-nums text-foreground">{overall_margin_pct}%</span>
          </div>
          <div className="flex items-center gap-tight text-meta border-l pl-snug">
            <span className="text-muted-foreground">Food Cost:</span>
            <span className="font-bold tabular-nums text-foreground">{food_cost_pct}%</span>
          </div>
          <span
            className={`inline-flex items-center gap-hair rounded px-snug py-hair text-[11px] font-semibold border ${
              coverageBadge === "Full Coverage"
                ? "bg-success-subtle text-success border-success/30"
                : coverageBadge === "Partial Coverage"
                ? "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30"
                : "bg-muted text-muted-foreground border-border"
            }`}
          >
            {coverageBadge} ({costedPct}% costed)
          </span>
        </div>
      </div>

      {coverageBadge !== "Full Coverage" && (
        <p className="text-[11px] text-muted-foreground bg-muted/30 px-snug py-hair rounded border">
          * {data_quality.costed_items_count || 0} of {data_quality.total_items_count || 0} active menu items have linked inventory recipes. Items without recipes use catalog defaults.
        </p>
      )}

      {/* 2x2 BCG Matrix Grid */}
      <div className="grid gap-base sm:grid-cols-2">
        {/* Quadrant 1: Stars */}
        <div className="rounded-md border border-success/30 bg-success-subtle/10 p-base space-y-snug">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-tight font-semibold text-body text-success">
              <Sparkles className="h-4 w-4" />
              <span>Stars (High Margin, High Volume)</span>
            </div>
            <span className="text-[11px] font-medium text-success">{stars.length} items</span>
          </div>
          <p className="text-[11px] text-muted-foreground">Protect quality, promote heavily, maintain recipe consistency</p>

          <div className="space-y-hair pt-tight border-t">
            {stars.slice(0, 4).map((item, idx) => (
              <div key={idx} className="flex items-center justify-between text-meta">
                <span className="font-medium text-foreground truncate max-w-[180px]">{item.name}</span>
                <div className="flex items-center gap-snug text-[11px] tabular-nums">
                  <span className="text-muted-foreground">{item.quantity} sold</span>
                  <span className="font-semibold text-success">{item.margin_pct}% mrg</span>
                </div>
              </div>
            ))}
            {stars.length === 0 && <p className="text-meta text-muted-foreground italic">No star items in period</p>}
          </div>
        </div>

        {/* Quadrant 2: Plowhorses */}
        <div className="rounded-md border border-primary/30 bg-primary/5 p-base space-y-snug">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-tight font-semibold text-body text-primary">
              <TrendingUp className="h-4 w-4" />
              <span>Plowhorses (Low Margin, High Volume)</span>
            </div>
            <span className="text-[11px] font-medium text-primary">{plowhorses.length} items</span>
          </div>
          <p className="text-[11px] text-muted-foreground">High popularity: consider modest price increases or recipe portion tuning</p>

          <div className="space-y-hair pt-tight border-t">
            {plowhorses.slice(0, 4).map((item, idx) => (
              <div key={idx} className="flex items-center justify-between text-meta">
                <span className="font-medium text-foreground truncate max-w-[180px]">{item.name}</span>
                <div className="flex items-center gap-snug text-[11px] tabular-nums">
                  <span className="text-muted-foreground">{item.quantity} sold</span>
                  <span className="font-semibold text-foreground">{item.margin_pct}% mrg</span>
                </div>
              </div>
            ))}
            {plowhorses.length === 0 && <p className="text-meta text-muted-foreground italic">No plowhorse items in period</p>}
          </div>
        </div>

        {/* Quadrant 3: Puzzles */}
        <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-base space-y-snug">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-tight font-semibold text-body text-amber-600 dark:text-amber-400">
              <Zap className="h-4 w-4" />
              <span>Puzzles (High Margin, Low Volume)</span>
            </div>
            <span className="text-[11px] font-medium text-amber-600 dark:text-amber-400">{puzzles.length} items</span>
          </div>
          <p className="text-[11px] text-muted-foreground">Great profitability: reposition on menu, train captains to suggest</p>

          <div className="space-y-hair pt-tight border-t">
            {puzzles.slice(0, 4).map((item, idx) => (
              <div key={idx} className="flex items-center justify-between text-meta">
                <span className="font-medium text-foreground truncate max-w-[180px]">{item.name}</span>
                <div className="flex items-center gap-snug text-[11px] tabular-nums">
                  <span className="text-muted-foreground">{item.quantity} sold</span>
                  <span className="font-semibold text-success">{item.margin_pct}% mrg</span>
                </div>
              </div>
            ))}
            {puzzles.length === 0 && <p className="text-meta text-muted-foreground italic">No puzzle items in period</p>}
          </div>
        </div>

        {/* Quadrant 4: Dogs */}
        <div className="rounded-md border border-muted bg-muted/20 p-base space-y-snug">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-tight font-semibold text-body text-muted-foreground">
              <AlertCircle className="h-4 w-4" />
              <span>Dogs (Low Margin, Low Volume)</span>
            </div>
            <span className="text-[11px] font-medium text-muted-foreground">{dogs.length} items</span>
          </div>
          <p className="text-[11px] text-muted-foreground">Underperforming: evaluate for menu removal or ingredient re-use</p>

          <div className="space-y-hair pt-tight border-t">
            {dogs.slice(0, 4).map((item, idx) => (
              <div key={idx} className="flex items-center justify-between text-meta">
                <span className="font-medium text-foreground truncate max-w-[180px]">{item.name}</span>
                <div className="flex items-center gap-snug text-[11px] tabular-nums">
                  <span className="text-muted-foreground">{item.quantity} sold</span>
                  <span className="font-semibold text-danger">{item.margin_pct}% mrg</span>
                </div>
              </div>
            ))}
            {dogs.length === 0 && <p className="text-meta text-muted-foreground italic">No dog items in period</p>}
          </div>
        </div>
      </div>
    </section>
  );
}
